//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';

import FacebookIcon from '@material-ui/icons/Facebook';
import PinterestIcon from '@material-ui/icons/Pinterest';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import LinkIcon from '@material-ui/icons/Link';
// import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import ShareIcon from '@material-ui/icons/Share';
import { Menu } from '@material-ui/core';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext } from '../../common/contexts';
import { getTwitterShareLink, getLinkedInShareLink, getPinterestShareLink, getFacebookShareLink, getShareWindowConfigStr, getJobSeekerShareUrl } from '../../common/linkHelper';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { copyTextToClipboard } from '../../system/helpers/systemHelper';
import XIcon from '../../assets/x_logo_black.png';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdButton from '../../common/elemental/AfitdButton';
// import AfitdIconButton from '../../common/elemental/AfitdIconButton';
import AfitdTypography from '../../common/elemental/AfitdTypography';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const styles = theme => ({
    root: {
        // display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        padding: '15px 0px',
    },
    menuSection: { width: 385, [theme.breakpoints.up('md')]: { width: 470 }, [theme.breakpoints.up('lg')]: { width: 670 } },
    // menuContentCopyIcon: { display: 'flex', alignContent: 'center', justifyContent: 'center', alignItems: 'center' },
    menuContainer: {
        display: 'flex',
        alignContent: 'center',
        justifyContent: 'center',
        alignItems: 'center',
    },
    shareButtonContainer: {
        display: 'flex',
        alignContent: 'center',
        justifyContent: 'center',
        alignItems: 'center',
    },
    shareButton: {
        height: 24,
        marginLeft: '10px',
        marginRight: '10px',
        marginBottom: '15px',
    },
    tweetButton: {
        height: 24,
    },
    pinButton: {
        height: 24,
    },
    copyButton: {
        height: 24,
        marginLeft: '10px',
        marginRight: '10px',
    },

    facebookIcon: {
        color: '#3B5998',
    },
    xIcon: {
        color: '#000',
    },
    pinterestIcon: {
        color: '#BD081C',
    },
    linkedInIcon: {
        color: '#2867B2',
    },
    closeIcon: {
        fontSize: '18px',
    },
});

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { props, shareUrl, snackbarMessage, setSnackbarMessage, handleCloseSnackbar } = params;

    const { classes, onClose = handleClickCloseButton } = props;
    const { shareButton: shareButtonCls, copyButton: copyButtonCls } = classes;

    // const TEMP_PROFILE_LINK = 'https://abyss1.page.link/y4HB';
    // const TEMP_PROFILE_MEDIA_LINK = 'https://abyss-1.web.app/pointy4.png';

    const defns = {
        shareButton: {
            startIcon: <FacebookIcon className={classes.facebookIcon} />,
            label: 'Share',
            fullWidth: false,
            size: 'small',
            onClick: handleClickShareButton.bind(null, shareUrl),
            classes: {
                root: shareButtonCls,
            },
        },
        tweetButton: {
            startIcon: (
                <span style={{ marginLeft: 2, marginRight: 3 }}>
                    <img src={XIcon} alt="x_icon" width="12px" />
                </span>
            ),
            // label: 'Post',
            fullWidth: false,
            size: 'small',
            onClick: handleClickTweetButton.bind(null, shareUrl),
            classes: {
                root: shareButtonCls,
            },
        },
        pinButton: {
            startIcon: <PinterestIcon className={classes.pinterestIcon} />,
            label: 'Pin',
            fullWidth: false,
            size: 'small',
            onClick: handleClickPinButton.bind(null, shareUrl /*, TEMP_PROFILE_MEDIA_LINK */),
            classes: {
                root: shareButtonCls,
            },
        },
        linkedInButton: {
            startIcon: <LinkedInIcon className={classes.linkedInIcon} />,
            label: 'LinkedIn',
            fullWidth: false,
            size: 'small',
            onClick: handleClickLinkedInButton.bind(null, shareUrl),
            classes: {
                root: shareButtonCls,
            },
        },
        copyButton: {
            startIcon: <LinkIcon />,
            label: 'Copy',
            fullWidth: false,
            size: 'small',
            onClick: handleClickCopyButton.bind(null, shareUrl, setSnackbarMessage),
            classes: {
                root: copyButtonCls,
            },
        },
        closeIconButton: {
            size: 'small',
            onClick: onClose,
        },
        snackbar: {
            message: { successMessage: snackbarMessage },
            onClose: handleCloseSnackbar,
        },
        shareText: {
            variant: 'body2',
            value: 'Share',
        },
        // root: { container: true },
        // menuContent: { item: true, lg: 1, md: 1, sm: 1, xs: 1 },
        // menuContentCopyIcon: { item: true, lg: 1, md: 1, sm: 1, xs: 1 },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const handleClickShareButton = shareUrl => {
    window.open(getFacebookShareLink(shareUrl), '_blank', getShareWindowConfigStr());
};

const handleClickTweetButton = shareUrl => {
    window.open(getTwitterShareLink(shareUrl), '_blank', getShareWindowConfigStr());
};

const handleClickPinButton = (shareUrl, shareMediaUrl) => {
    window.open(getPinterestShareLink(shareUrl, shareMediaUrl), '_blank', getShareWindowConfigStr());
};

const handleClickLinkedInButton = shareUrl => {
    window.open(getLinkedInShareLink(shareUrl), '_blank', getShareWindowConfigStr());
};

const handleClickCopyButton = (shareUrl, setSnackbarMessage) => {
    copyTextToClipboard(shareUrl, setSnackbarMessage);
    // console.log('%c@@@@ handleClickCopyButton START', 'background: #222; color: #bada55');
};

const handleClickCloseButton = () => {
    // console.log('%c@@@@ handleClickCloseButton START', 'background: #222; color: #bada55');
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
// Since this component is used in the MUI Slide, it has to be able to hold a ref.
//   Therefore, we cannot use function component.
class ShareButtonMenu extends Component {
    state = {
        snackbarMessage: null,
    };

    setSnackbarMessage = message => {
        this.setState({ snackbarMessage: message });
    };

    handleCloseSnackbar = () => {
        this.setState({ snackbarMessage: null });
    };

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    render() {
        //---------------------------------------------------------------------
        // From context
        //---------------------------------------------------------------------
        const appContext = this.context;
        const { currentUser, userId } = appContext;

        //---------------------------------------------------------------------
        // From props
        //---------------------------------------------------------------------

        //---------------------------------------------------------------------
        // Derived
        //---------------------------------------------------------------------
        const urlName = _get(currentUser, 'userName');
        const shareUrl = getJobSeekerShareUrl({ urlName, userId });

        //---------------------------------------------------------------------
        // Defns
        //---------------------------------------------------------------------
        const defns = getDefns({
            props: this.props,
            shareUrl,
            snackbarMessage: this.state.snackbarMessage,
            setSnackbarMessage: this.setSnackbarMessage,
            handleCloseSnackbar: this.handleCloseSnackbar,
        });
        const { handleOpenMenu, handleCloseMenu, open, anchorEl, smMatches } = this.props;

        return (
            <div {...defns.menuContainer}>
                <div data-tour="js-share-button-container" {...defns.shareButtonContainer}>
                    <div aria-expanded={open ? 'true' : undefined} onClick={handleOpenMenu}>
                        <div {...defns.shareContainer}>
                            <IconButton {...defns.shareIcon}>
                                <ShareIcon />
                            </IconButton>
                        </div>
                    </div>
                    <div aria-expanded={open ? 'true' : undefined} onClick={handleOpenMenu}>
                        {smMatches ? null : <AfitdTypography {...defns.shareText} />}
                    </div>
                </div>

                <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleCloseMenu}
                    MenuListProps={{
                        'aria-labelledby': 'basic-button',
                    }}
                    {...defns.menuSection}
                >
                    <div {...defns.root}>
                        {/* <Grid {...defns.menuContent}>
                            <AfitdButton {...defns.shareButton}>Share</AfitdButton>
                        </Grid> */}
                        <div {...defns.menuContent}>
                            <AfitdButton {...defns.tweetButton}>Post</AfitdButton>
                        </div>
                        {/* <Grid {...defns.menuContent}>
                            <AfitdButton {...defns.pinButton}>Pin</AfitdButton>
                        </Grid> */}
                        <div {...defns.menuContent}>
                            <AfitdButton {...defns.linkedInButton}>LinkedIn</AfitdButton>
                        </div>
                        <div {...defns.menuContent}>
                            <AfitdButton {...defns.copyButton}>Copy</AfitdButton>
                        </div>
                    </div>
                    {/* <div {...defns.root}>
                        <AfitdButton {...defns.shareButton}>Share</AfitdButton>
                        <AfitdButton {...defns.tweetButton}>Tweet</AfitdButton>
                        <AfitdButton {...defns.pinButton}>Pin</AfitdButton>
                        <AfitdButton {...defns.linkedInButton}>LinkedIn</AfitdButton>
                        <AfitdButton {...defns.copyButton}>Copy</AfitdButton>
                    </div> */}
                </Menu>
            </div>
        );
    }
}

ShareButtonMenu.contextType = AppContext;

export default withStyles(styles)(ShareButtonMenu);
